import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import type { Binary } from 'mongodb';
import { DeclareMethod } from '/imports/methods';
import { Doc, ListMethodArg, ListMethodResponse, GetMethodResponse, ModelClass, References } from '/imports/types';

export interface Escrow extends Doc {
  /** How an eager trade actually settled (stamped by the eager verify). */
  settlement?: EscrowSettlement;
  _id: string;
  key: Binary;
  botId: number;
  bot: string;
  partnerId: number;
  partner: string;
  tradeno: number | null;
  closed: number | null;
  completed: boolean;
  created?: number;
}

type Model = Escrow;
type Refs = References<Model>;

export const Escrows = new Mongo.Collection<Escrow>('escrows');

/**
 * Trader analytics group and filter by `partnerId` across ~370k rows going back
 * to 2018, and the collection has no index but `_id` -- so every one of those
 * queries is a full scan today.
 *
 * Fire-and-forget outside `Meteor.startup` on purpose: a rejecting startup hook
 * calls `process.exit(1)`, and this collection is large enough that an awaited
 * build there could delay or kill boot.
 */
Meteor.isServer && (async () => {
  try {
    const raw = Escrows.rawCollection();
    await raw.createIndex({ partnerId: 1 });
    await raw.createIndex({ botId: 1 });
  }
  catch (e: any) {
    console.warn('[escrows] index creation failed', e?.message ?? e);
  }
})();

export const listEscrows = DeclareMethod<ListMethodArg<Escrow>, ListMethodResponse<Escrow, Refs>>('listEscrows');
export const getEscrow = DeclareMethod<string, GetMethodResponse<Escrow, Refs>>('getEscrow');
export const createEscrow = DeclareMethod<Partial<Escrow>, string>('createEscrow');
export const updateEscrow = DeclareMethod<Partial<Escrow>, string>('updateEscrow');
export const deleteEscrow = DeclareMethod<string, undefined>('deleteEscrow');

export const modelClass: ModelClass<Model, Refs> = {
  collection: Escrows,
  editableFields: ['tradeno', 'closed', 'completed'],
  listMethod: listEscrows,
  getMethod: getEscrow,
  createMethod: createEscrow,
  updateMethod: updateEscrow,
  deleteMethod: deleteEscrow,
  pathPrefix: '/escrows',
  modelName: 'Escrow',
  pluralName: 'Escrows',
};

/** What an eager trade actually settled at, kept beside the ledger rows so
 *  nothing is lost: the rows carry the settled price plus our own ask/bid and
 *  the partner's quote; this carries the trade-level money and credit. */
export interface EscrowSettlement {
  direction: 'sell' | 'buy' | 'hybrid';
  /** What our cards fetched: tickets plus credit banked (sell leg). */
  consideration: number;
  /** Tickets received (sell) or net received (hybrid; negative = paid). */
  tickets: number;
  /** The partner's stated total for our cards. */
  partnerTally: number | null;
  /** Our credit balance at their store as they stated it at the start. */
  creditBefore: number | null;
  /** Our credit they applied to this payment ("... + 0.53 saved credit"). */
  creditClaimed: number | null;
  /** Our balance after, from their post-trade statement. */
  creditHeld: number | null;
  /** Per-card prices came from their quotes, or pro-rata on our asks. */
  allocation: 'quotes' | 'pro-rata';
  at: Date;
}
